import React, { useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";

import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";
import { FormLabel, FormHelperText, formStyles } from "./FormUtils";

const CustomDateInput = ({
  // Basic Props
  id,
  name,
  label,
  placeholder = "Select a date",
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,

  // Validation Props
  isRequired = false,
  error,
  errorMessage,
  validateTodayAndPast = false,
  min,
  max,

  // Style Props
  className = "",
  labelStyle = "",
  outerContainerStyle = "",
  helpTextStyle = "",
  errorTextStyle = "",
  errorContainerStyle = "",

  // Helper Props
  helpText,
  disabled = false,
  readOnly = false,
  autoFocus = false,

  // Special Props
  isLocalUTC = false,
  isFilterSearch = false,

  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const getBorderClasses = () => {
    if (error) return "border-red-500";
    if (isFocused) return "border-light-blue-2";
    return "border-[#808080]";
  };

  const handleChange = (newValue) => {
    if (!newValue) {
      if (onChange) {
        onChange({ target: { name, value: "" } });
      }
      return;
    }

    const formatted = newValue.format("YYYY-MM-DD");
    if (onChange) {
      onChange({ target: { name, value: formatted } });
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) onBlur();
  };

  // Convert min/max to dayjs objects if provided
  const minDate = min ? dayjs(min) : null;
  const maxDate = max ? dayjs(max) : null;

  // Determine if future dates should be disabled
  const shouldDisableFuture = validateTodayAndPast;

  // Get current date in appropriate timezone
  const getTodayDate = () => {
    const today = new Date();
    if (isLocalUTC) {
      return dayjs.utc(today);
    }
    return dayjs(today);
  };

  const currentDate = getTodayDate();

  return (
    <div className={twMerge("flex flex-col w-full mb-1", outerContainerStyle)}>
      {/* Label and Error Message Row */}
      <div className="flex items-center gap-2">
        <FormLabel
          htmlFor={id}
          label={label}
          isRequired={isRequired}
          className={labelStyle}
          helpText={helpText}
        />
        {errorMessage && (
          <span className="text-[#C00707] text-[12px] leading-[22px] font-['Public_Sans'] font-normal mb-2 ml-2 whitespace-nowrap">
            {errorMessage}
          </span>
        )}
      </div>

      {/* Help Text */}
      {helpText && (
        <span className={twMerge(formStyles.helpText, helpTextStyle)}>
          {helpText}
        </span>
      )}

      {/* Date Picker */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          disableFuture={shouldDisableFuture}
          minDate={minDate}
          maxDate={shouldDisableFuture ? currentDate : maxDate}
          slotProps={{
            textField: {
              id,
              name,
              placeholder,
              autoFocus,
              onFocus: handleFocus,
              onBlur: handleBlur,
              className: twMerge(
                "w-full h-10 rounded-md font-public-sans font-normal text-[10px] sm:text-[.9375rem] text-[#686868] placeholder:text-[#686868] placeholder:text-opacity-80 focus:outline-none px-4",
                getBorderClasses(),
                className
              ),
              error: !!error,
              InputProps: {
                style: {
                  height: "40px",
                  minHeight: "40px",
                  padding: "0",
                },
              },
              ...rest,
            },
            popper: {
              sx: {
                "& .MuiPaper-root": {
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                },
              },
            },
          }}
          sx={{
            width: "100%",
            height: "40px",
            "& .MuiInputBase-root": {
              height: "46px !important",
              minHeight: "46px !important",
              borderRadius: "8px",
              backgroundColor: "#45444433 !important",
              fontFamily: "Public Sans, sans-serif",
              fontSize: "clamp(10px, 2vw, 15px)",
              color: "#686868",
              padding: "0 16px",
              "& fieldset": {
                borderColor: "#808080",
              },
              "&:hover fieldset": {
                borderColor: "#808080",
              },
              "&:focused fieldset": {
                borderColor: "#808080",
              }
            },
            "& .MuiInputBase-input": {
              height: "40px !important",
              paddingLeft: "20px !important",
              paddingRight: "20px !important",
              margin: "0",
              display: "flex",
              alignItems: "center",
              color: "#686868",
              fontSize: "clamp(10px, 2vw, 15px)",
              fontFamily: "Public Sans, sans-serif",
              fontWeight: "normal",
              backgroundColor: "transparent !important",
              "&::placeholder": {
                color: "#686868",
                opacity: 0.8,
                fontSize: "clamp(10px, 2vw, 15px)",
              },
            },
            "& .MuiInputAdornment-root": {
              height: "40px",
              maxHeight: "40px",
              margin: "0",
            },
            "& .MuiSvgIcon-root": {
              color: "#686868",
              fontSize: "20px",
              margin: "0",
            },
            "& .MuiIconButton-root": {
              padding: "0",
              margin: "0",
              marginRight: "10px !important",
              height: "40px",
              width: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .MuiPaper-root": {
              backgroundColor: "#262530",
              color: "#e2e2e2",
              border: "1px solid #505050",
            },
            "&& .MuiOutlinedInput-root": {
              backgroundColor: "#262530",
              color: "#e2e2e2",
            },
          }}
        />
      </LocalizationProvider>

      {/* Helper Text and Error Display */}
      {helpText || (!isFilterSearch && error) ? (
        <FormHelperText
          helpText={helpText}
          error={!isFilterSearch ? error : null}
          className={errorContainerStyle}
          errorClassName={errorTextStyle}
        />
      ) : (
        <span className={twMerge(errorContainerStyle)} />
      )}
    </div>
  );
};

export default CustomDateInput;
